import { createFileRoute, Link } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useStudentFocus } from '@/admin/studentFocus';
import type { BadgeVariant } from '@/admin/ui/Badge';
import { Badge } from '@/admin/ui/Badge';
import { FilterBanner } from '@/admin/ui/Form';
import { PageTitle } from '@/admin/ui/Page';
import { LIST_PAGE_SIZE, Pagination } from '@/admin/ui/Pagination';
import { Table } from '@/admin/ui/Table';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import i18n from '~/i18n';

type GradingSubmissionSummary =
  components['schemas']['GradingSubmissionSummary'];

type FeedbackSearch = {
  quiz_response_id?: number;
};

export const Route = createFileRoute('/admin/feedbacks/')({
  staticData: { minTier: 'ADMIN' },
  component: FeedbacksList,
  validateSearch: (search: Record<string, unknown>): FeedbackSearch => {
    const raw = search.quiz_response_id;
    const id =
      typeof raw === 'number'
        ? raw
        : typeof raw === 'string' && raw !== ''
          ? Number(raw)
          : undefined;
    return {
      quiz_response_id:
        id !== undefined && Number.isFinite(id) ? id : undefined,
    };
  },
});

const columnHelper = createColumnHelper<GradingSubmissionSummary>();

function statusBadge(status: string): {
  label: string;
  variant: BadgeVariant;
} {
  switch (status) {
    case 'GRADED':
      return {
        label: i18n.t('admin.feedbacks.status.graded'),
        variant: 'success',
      };
    case 'PENDING':
      return {
        label: i18n.t('admin.feedbacks.status.pending'),
        variant: 'warning',
      };
    case 'FAILED':
      return {
        label: i18n.t('admin.feedbacks.status.failed'),
        variant: 'error',
      };
    default:
      return { label: status, variant: 'neutral' };
  }
}

const columns = [
  columnHelper.accessor('id', { header: () => i18n.t('admin.common.id') }),
  columnHelper.accessor((row) => row.student.display_name, {
    id: 'student',
    header: () => i18n.t('admin.feedbacks.col.student'),
  }),
  columnHelper.accessor((row) => row.course.title, {
    id: 'course',
    header: () => i18n.t('admin.feedbacks.col.course'),
  }),
  columnHelper.accessor((row) => row.lesson.title, {
    id: 'lesson',
    header: () => i18n.t('admin.feedbacks.col.lesson'),
  }),
  columnHelper.accessor('score', {
    header: () => i18n.t('admin.feedbacks.col.score'),
    cell: (info) => `${info.getValue()}%`,
  }),
  columnHelper.accessor('attempt_number', {
    header: () => i18n.t('admin.feedbacks.col.attempt'),
  }),
  columnHelper.accessor('status', {
    header: () => i18n.t('admin.common.status'),
    cell: (info) => {
      const { label, variant } = statusBadge(info.getValue());
      return <Badge $variant={variant}>{label}</Badge>;
    },
  }),
  columnHelper.accessor('graded_at', {
    header: () => i18n.t('admin.feedbacks.col.graded'),
    cell: (info) => {
      const v = info.getValue();
      return v ? new Date(v).toLocaleString() : '—';
    },
  }),
];

function FeedbacksList() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const [page, setPage] = useState(0);
  const { focusedStudent } = useStudentFocus();

  const { data, isLoading, error } = api.adminListFeedbackSubmissions.useQuery({
    query: {
      limit: LIST_PAGE_SIZE,
      offset: LIST_PAGE_SIZE * page,
      ...(search.quiz_response_id !== undefined
        ? { quiz_response_id: search.quiz_response_id }
        : {}),
      ...(focusedStudent !== null ? { student_id: focusedStudent.id } : {}),
    },
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      <PageTitle>{t('admin.nav.feedbacks')}</PageTitle>

      {search.quiz_response_id !== undefined ? (
        <FilterBanner>
          <span>
            {t('admin.feedbacks.filteredToSubmission')}{' '}
            <Link
              to="/admin/quiz-submissions/$submissionId/"
              params={{ submissionId: String(search.quiz_response_id) }}
            >
              #{search.quiz_response_id}
            </Link>
          </span>
          <Link to="/admin/feedbacks/" search={{}}>
            {t('admin.common.clearFilter')}
          </Link>
        </FilterBanner>
      ) : null}

      {isLoading ? (
        <LoadingMessage>{t('admin.feedbacks.loading')}</LoadingMessage>
      ) : error ? (
        <ErrorMessage>{t('admin.feedbacks.errorLoading')}</ErrorMessage>
      ) : (
        <>
          <Pagination
            page={page}
            pageSize={LIST_PAGE_SIZE}
            shown={data?.data.length ?? 0}
            total={data?.count}
            onPage={setPage}
          />

          <Table
            table={table}
            to="/admin/feedbacks/$submissionId/"
            getParams={(row) => ({ submissionId: String(row.id) })}
          />
          <Pagination
            page={page}
            pageSize={LIST_PAGE_SIZE}
            shown={data?.data.length ?? 0}
            total={data?.count}
            onPage={setPage}
            hideWhenAlone
          />
        </>
      )}
    </div>
  );
}

const LoadingMessage = styled.div`
  color: var(--grey-600);
  padding: 2rem;
  text-align: center;
`;

const ErrorMessage = styled.div`
  color: var(--error);
  padding: 2rem;
  text-align: center;
`;
